import { memo, ReactNode } from 'react'
import React from 'react'

import { isWithinInterval } from 'date-fns'
import { useTheme } from 'styled-components'

import { SwipeableRow } from '@components/SwipeableRow'
import { useSharedDates } from '@contexts/sharedDates'
import { useDate } from '@hooks/dates'
import { useRoles } from '@hooks/roles'
import { useUpdateTaskAndTaskList } from '@hooks/task/put/editResourceAndList'
import { IGenericTask } from '@interfaces/task/generic/IGenericTask'

interface IDownstreamSwipeProps {
  children: ReactNode
  task: IGenericTask
}

export const DownstreamSwipe = memo(function DownstreamSwipe({
  children,
  task,
}: IDownstreamSwipeProps) {
  const theme = useTheme()

  const { mutateAsync: putTask } = useUpdateTaskAndTaskList()
  const { readOnlyUserRoles, isUserAllowed } = useRoles()

  const { getWeekRange } = useDate()

  const { selectedDate } = useSharedDates()

  const isReadOnlyUser = isUserAllowed(readOnlyUserRoles)

  const { weekStart: currentWeekStart, weekEnd: currentWeekEnd } = getWeekRange(
    new Date(),
  )

  const isSelectedDateBetweenRange = isWithinInterval(selectedDate, {
    start: currentWeekStart,
    end: currentWeekEnd,
  })

  function handleSwipe() {
    const swipeActionsDownstream = []

    task.status !== 'completed' &&
      isSelectedDateBetweenRange &&
      swipeActionsDownstream.push({
        color: theme.colors.taskStatus.completed,
        text: 'Complete',
        onPress: setTaskCompleted,
      })

    task.status !== 'scheduled' &&
      task.status === 'completed' &&
      isSelectedDateBetweenRange &&
      swipeActionsDownstream.push({
        color: theme.colors.taskStatus.scheduled,
        text: 'Not Complete',
        onPress: setTaskScheduled,
      })

    task.status !== 'ready' &&
      isSelectedDateBetweenRange &&
      swipeActionsDownstream.push({
        color: theme.colors.taskStatus.ready,
        text: 'Permit Ready',
        onPress: setTaskReady,
      })

    task.status !== 'not-ready' &&
      task.status !== 'completed' &&
      isSelectedDateBetweenRange &&
      swipeActionsDownstream.push({
        color: theme.colors.taskStatus.notReady,
        text: 'Permit Not Ready',
        onPress: setTaskNotReady,
      })

    return swipeActionsDownstream
  }

  function setTaskNotReady() {
    const changes = {
      status: 'not-ready',
      permitStatus: 0,
      permitNeeded: 1,
      isComplete: false,
    }
    handlePutUpdateTask(changes)
  }

  function setTaskReady() {
    const changes = { status: 'ready', permitStatus: 1, isComplete: false }
    handlePutUpdateTask(changes)
  }

  function setTaskCompleted() {
    const changes = { status: 'completed', isComplete: true }
    handlePutUpdateTask(changes)
  }

  function setTaskScheduled() {
    const changes = { status: 'scheduled', isComplete: false }
    handlePutUpdateTask(changes)
  }

  function handlePutUpdateTask(changes: Partial<IGenericTask>) {
    putTask({
      id: task.id,
      task: { ...task, ...changes },
    })
  }

  if (isReadOnlyUser) {
    return <>{children}</>
  }

  return <SwipeableRow rightActions={handleSwipe()}>{children}</SwipeableRow>
})
